/**
 * Statutory public holidays in Germany per Bundesland (GOD-50), for the
 * calendar. Only holidays that apply to a whole Bundesland are listed –
 * Fronleichnam in parts of Saxony/Thuringia, Mariä Himmelfahrt in Catholic
 * Bavarian municipalities and the Augsburger Friedensfest are left out.
 * Without a region only the nationwide holidays are returned.
 */
export const HOLIDAY_REGIONS = ["BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV", "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH"] as const;
export type HolidayRegion = (typeof HOLIDAY_REGIONS)[number];

export interface PublicHoliday {
    /** YYYY-MM-DD */
    date: string;
    name: string;
}

/** Easter Sunday (Gregorian, anonymous algorithm) as a UTC date. */
export function easterSunday(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day));
}

const iso = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (date: Date, days: number) => new Date(date.getTime() + days * 86400000);
const fixed = (year: number, month: number, day: number) => new Date(Date.UTC(year, month - 1, day));

/** Buß- und Bettag: the Wednesday before 23 November. */
function repentanceDay(year: number): Date {
    const nov23 = fixed(year, 11, 23);
    const back = ((nov23.getUTCDay() - 3 + 7) % 7) || 7;
    return addDays(nov23, -back);
}

export function isHolidayRegion(value: unknown): value is HolidayRegion {
    return typeof value === "string" && (HOLIDAY_REGIONS as readonly string[]).includes(value);
}

export function germanHolidays(year: number, region?: string | null): PublicHoliday[] {
    const easter = easterSunday(year);
    const r = isHolidayRegion(region) ? region : null;
    const inRegion = (...regions: HolidayRegion[]) => r !== null && regions.includes(r);

    const list: [Date, string][] = [
        [fixed(year, 1, 1), "Neujahr"],
        [addDays(easter, -2), "Karfreitag"],
        [addDays(easter, 1), "Ostermontag"],
        [fixed(year, 5, 1), "Tag der Arbeit"],
        [addDays(easter, 39), "Christi Himmelfahrt"],
        [addDays(easter, 50), "Pfingstmontag"],
        [fixed(year, 10, 3), "Tag der Deutschen Einheit"],
        [fixed(year, 12, 25), "1. Weihnachtstag"],
        [fixed(year, 12, 26), "2. Weihnachtstag"],
    ];
    if (inRegion("BW", "BY", "ST")) list.push([fixed(year, 1, 6), "Heilige Drei Könige"]);
    if (inRegion("BE") && year >= 2019) list.push([fixed(year, 3, 8), "Internationaler Frauentag"]);
    if (inRegion("MV") && year >= 2023) list.push([fixed(year, 3, 8), "Internationaler Frauentag"]);
    if (inRegion("BB")) {
        list.push([easter, "Ostersonntag"]);
        list.push([addDays(easter, 49), "Pfingstsonntag"]);
    }
    if (inRegion("BW", "BY", "HE", "NW", "RP", "SL")) list.push([addDays(easter, 60), "Fronleichnam"]);
    if (inRegion("SL")) list.push([fixed(year, 8, 15), "Mariä Himmelfahrt"]);
    if (inRegion("TH") && year >= 2019) list.push([fixed(year, 9, 20), "Weltkindertag"]);
    // Reformationstag: in the north since 2018 (2017 was a nationwide one-off).
    if (year === 2017 || inRegion("BB", "MV", "SN", "ST", "TH") || (inRegion("HB", "HH", "NI", "SH") && year >= 2018)) {
        list.push([fixed(year, 10, 31), "Reformationstag"]);
    }
    if (inRegion("BW", "BY", "NW", "RP", "SL")) list.push([fixed(year, 11, 1), "Allerheiligen"]);
    if (inRegion("SN")) list.push([repentanceDay(year), "Buß- und Bettag"]);

    return list.map(([date, name]) => ({ date: iso(date), name })).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Holidays per day for the calendar: the statutory ones of the region for the
 * given years plus the ones imported from the sermon plan ("Feiertage" sheet,
 * also church days like Muttertag). The same name on the same day is shown once.
 */
export function calendarHolidays(
    years: number[],
    region: string | null | undefined,
    imported: { date: string; name: string }[] = []
): Map<string, string[]> {
    const byDay = new Map<string, string[]>();
    const add = (date: string, name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const names = byDay.get(date) ?? [];
        if (!names.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())) names.push(trimmed);
        byDay.set(date, names);
    };
    for (const year of Array.from(new Set(years))) {
        for (const holiday of germanHolidays(year, region)) add(holiday.date, holiday.name);
    }
    for (const holiday of imported) add(holiday.date, holiday.name);
    return byDay;
}
